import React, { useState, useEffect } from 'react';
import { getEvidenceUrl, downloadEvidence } from './downloadFromSDK.module';
import { useTenancy } from '@/contexts/TenancyContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Info, FileText } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toLocalDateString, cleanDateAndTimeObject } from '@shared/renderDate';
import { useUser } from '@/contexts/UserContextProvider';

interface TimestampLike {
  seconds?: number;
  nanoseconds?: number;
  toDate?: () => Date;
  __time__?: string;
}

interface TaskHistoryItem {
  changeID: number;
  changedBy: string;
  dateChanged: any;
  fieldsChanged: Record<string, any>;
  [key: string]: any;
}

interface PerformHistoryItem {
  [key: string]: any;
  assignedTo?: string;
  check?: string;
  checkDate?: TimestampLike;
  checkStatus?: string;
  comments?: string;
  dataClassification?: string;
  evidence?: string | string[];
  lastCheck?: TimestampLike;
  performedBy?: string;
  performedDate?: TimestampLike;
  scheduledDate?: TimestampLike;
  checkDetail?: {
    active: string;
    assignedTo: string;
    check: string;
    evidence: string | string[];
    [key: string]: any;
  };
}

interface TaskHistoryDialogProps {
  editHistory: Record<string, TaskHistoryItem>;
  performHistory: Record<string, PerformHistoryItem>;
  isOpen: boolean;
  onClose: () => void;
  getUserName?: (userId: string) => Promise<string>;
}

// Robust date formatter for all weird shapes
const formatAnyDate = (date: any): string => {
  if (!date) return 'Unknown date';
  // Firestore __time__ object
  if (typeof date === 'object' && date.__time__ !== undefined) {
    if (!date.__time__) return 'No date recorded';
    const d = new Date(date.__time__);
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    return 'Unknown date';
  }
  // Firebase Timestamp
  if (typeof date === 'object' && date.toDate && typeof date.toDate === 'function') {
    const d = date.toDate();
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    return 'Unknown date';
  }
  // Timestamp-like object
  if (typeof date === 'object' && date.seconds && date.nanoseconds) {
    const d = new Date(date.seconds * 1000 + Math.floor(date.nanoseconds / 1e6));
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    return 'Unknown date';
  }
  // ISO string or readable string
  if (typeof date === 'string') {
    // Try ISO/parseable
    const d = new Date(date);
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    // If it looks like a date (contains month and year), just show it
    if (/\w+ \d{1,2}, \d{4}/.test(date) || /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(date) || /\d{4}-\d{2}-\d{2}/.test(date)) {
      return date;
    }
    return 'Unknown date';
  }
  // Number (epoch ms or seconds)
  if (typeof date === 'number') {
    let d;
    if (date < 1e12) d = new Date(date * 1000);
    else d = new Date(date);
    if (!isNaN(d.getTime())) return toLocalDateString(d);
    return 'Unknown date';
  }
  // Try to new Date() as fallback
  try {
    const d = new Date(date);
    if (!isNaN(d.getTime())) return toLocalDateString(d);
  } catch { }
  return 'Unknown date';
};

// Deep equals for objects/arrays
const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object' || a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(k => deepEqual(a[k], b[k]));
};

const TaskHistoryDialog: React.FC<TaskHistoryDialogProps> = ({ editHistory, performHistory, isOpen, onClose }) => {
  const { userIdToName, users } = useTenancy();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState({
    editHistory: true,
    performHistory: true
  });

  // Reset expanded states when dialog is closed or when task changes
  useEffect(() => {
    // Reset states when dialog opens/closes or when task changes
    setExpanded([]);
    setExpandedSections({
      editHistory: true,
      performHistory: true
    });
  }, [isOpen, editHistory, performHistory]);

  const toggleSection = (section: 'editHistory' | 'performHistory') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  console.log('[TaskHistoryDialog] editHistory:', editHistory);
  console.log('[TaskHistoryDialog] performHistory:', performHistory);

  // Helper to get user display name by id
  const getUserDisplayName = (userId: string) => {
    if (userIdToName[userId]) return userIdToName[userId];
    const user = users.find(u => u.id === userId);
    if (user) return `${user.firstName} ${user.lastName}`;
    return false;
  };

  // Render value for framework and similar objects
  const renderFieldValue = (field: string, val: any): string => {
    console.log('Field:', field, 'Value:', val);
    if (field.toLowerCase().includes('framework')) {
      // Try to extract frameName(s)
      if (typeof val === 'object' && val !== null) {
        let result;
        if (Array.isArray(val)) {
          result = val.map(v => (v && typeof v === 'object' && 'frameName' in v && v.frameName) ? v.frameName : JSON.stringify(v)).join(', ');
        }
        // Sometimes it's an object with numeric keys
        else if (Object.values(val).every(v => v && typeof v === 'object' && 'frameName' in v)) {
          result = Object.values(val).map((v: any) => v && v.frameName ? v.frameName : JSON.stringify(v)).join(', ');
        }
        else if ('frameName' in val && val.frameName) {
          result = val.frameName;
        } else {
          result = JSON.stringify(val);
        }
        // Convert to uppercase for framework fields
        return typeof result === 'string' ? result.toUpperCase() : result;
      }
      // If it's a string but not an object, still convert to uppercase
      else if (typeof val === 'string') {
        return val.toUpperCase();
      }
    }
    if (field.toLowerCase().includes('assignedto')) {
      return getUserDisplayName(val) || 'Unknown User';
    }
    // Skip rendering delete-related fields as they'll be handled specially
    if (['isdeleted', 'deletedat', 'deletedby'].includes(field.toLowerCase())) {
      return '';
    }
    // Inline the robust value formatting logic here
    if (val == null) return '';
    if (typeof val === 'object') {
      if (val.toDate || val.__time__ || val.seconds || val.nanoseconds) {
        return formatAnyDate(val);
      }
      if (val.constructor && val.constructor.name === 'Timestamp') {
        try { return formatAnyDate(val); } catch { }
      }
      try { return JSON.stringify(val); } catch { return '[object Object]'; }
    }
    if (typeof val === 'number' && val > 1000000000) {
      return formatAnyDate(val);
    }
    return String(val);
  };

  // Check if an edit entry represents a delete/restore event
  const getDeleteRestoreEvent = (entry: TaskHistoryItem): { type: 'deleted' | 'restored' | null; user: string; date: any } | null => {
    if (!entry.fieldsChanged) return null;
    
    const isDeletedField = Object.entries(entry.fieldsChanged).find(([field, details]) => 
      field.toLowerCase() === 'isdeleted' && !deepEqual(details.oldValue, details.newValue)
    );
    
    if (!isDeletedField) return null;
    
    const [, details] = isDeletedField;
    const newValue = details.newValue;
    
    if (newValue === true || newValue === 'true') {
      return {
        type: 'deleted',
        user: getUserDisplayName(entry.changedBy) || 'Unknown User',
        date: entry.dateChanged
      };
    } else if (newValue === false || newValue === 'false') {
      return {
        type: 'restored',
        user: getUserDisplayName(entry.changedBy) || 'Unknown User',
        date: entry.dateChanged
      };
    }
    
    return null;
  };

  // Sort entries by date descending using raw timestamp values
  const getTimestampMs = (dateChanged: any) => {
    if (!dateChanged) return 0;
    if (typeof dateChanged === 'object' && typeof dateChanged.seconds === 'number') {
      return dateChanged.seconds * 1000 + Math.floor(dateChanged.nanoseconds / 1e6);
    }
    if (typeof dateChanged === 'number') {
      // Heuristic: treat as ms if large, else seconds
      return dateChanged < 1e12 ? dateChanged * 1000 : dateChanged;
    }
    if (typeof dateChanged === 'string') {
      const d = new Date(dateChanged);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }
    return 0;
  };

  const sortedEntries = Object.entries(editHistory)
    .filter(([key]) => key !== 'historyCount')
    .sort((a, b) => (cleanDateAndTimeObject(b[1]?.dateChanged, true).getTime() || getTimestampMs(b[1]?.dateChanged)) - (cleanDateAndTimeObject(a[1]?.dateChanged, true).getTime() || getTimestampMs(a[1]?.dateChanged)));

  // Convert performHistory to an array of entries, handling both object and array formats
  const performEntries = Array.isArray(performHistory)
    ? performHistory.map((item, index) => [index.toString(), item])
    : Object.entries(performHistory);

  const getPerformTimestampMs = (dateObj: any) => {
    // Try to use cleanDateAndTimeObject if available, else fallback to getTimestampMs
    if (!dateObj) return 0;
    const cleaned = cleanDateAndTimeObject(dateObj, true);
    if (cleaned && typeof cleaned.getTime === 'function') {
      const t = cleaned.getTime();
      if (!isNaN(t)) return t;
    }
    return getTimestampMs(dateObj);
  };

  // Helper to detect 'empty' perform entries
  const isEmptyPerformEntry = (entry: any) => {
    // All fields are empty string, or performedDate is epoch (or missing), and no meaningful check/comments/data
    const emptyStringFields = [
      entry.check,
      entry.dataClassification,
      entry.evidence,
      entry.assignedTo,
      entry.comments
    ].every(v => v === '' || v == null || (Array.isArray(v) && v.length === 0));
    // performedDate is epoch (e.g., 1072915200) or missing
    const performedDate = entry.performedDate;
    const isEpoch = performedDate && typeof performedDate.seconds === 'number' && performedDate.seconds < 2000000000;
    return emptyStringFields && isEpoch;
  };

  const sortedPerformEntries = performEntries
    .filter(([key, entry]) => key !== 'historyCount' && key !== 'numberPreviousChecks' && key !== 'active' && !isEmptyPerformEntry(entry))
    .map(([key, entry]) => {
      // Only use performedDate for sorting
      const performedDate = entry.performedDate;
      const sortDate = performedDate ? getPerformTimestampMs(performedDate) : 0;
      return { key, entry, sortDate };
    })
    .sort((a, b) => b.sortDate - a.sortDate)
    .map(({ key, entry }) => [key, entry]);

  console.log('sortedPerformEntries:', sortedPerformEntries);

  const toggleExpand = (key: string, type: 'edit' | 'perform' = 'edit') => {
    const fullKey = `${type}-${key}`;
    setExpanded(prev => prev.includes(fullKey) ? prev.filter(k => k !== fullKey) : [...prev, fullKey]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* <DialogContent className="w-full max-w-3xl sm:max-w-4xl md:max-w-5xl lg:max-w-6xl bg-white dark:bg-secondary" style={{ maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} aria-describedby={undefined}> */}
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card overflow-hidden">
          <div className="sticky top-0 z-20 p-4 py-6 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle>Task History</DialogTitle>
            <DialogDescription>
              View the perform history and edit history for this task.
            </DialogDescription>
          </div>

          <div className="space-y-4 w-full flex-1 overflow-hidden px-4">
              {/* Edit History Section */}
              <div className="border rounded-md overflow-hidden">
                <button
                  onClick={() => toggleSection('editHistory')}
                  className="w-full text-left bg-primary/15 dark:bg-primary/20 text-primary p-3 font-medium text-sm flex justify-between items-center hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
                  aria-expanded={expandedSections.editHistory}
                >
                  <span>Edit History</span>
                  {expandedSections.editHistory ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                {sortedEntries.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No history available for this task.
                  </div>
                ) : expandedSections.editHistory ? (
                  <div className="flex flex-col">
                    <div className="divide-y">
                      {sortedEntries.map(([key, entry]) => {
                        const historyEntry = entry as TaskHistoryItem;
                        // Debug print for dateChanged
                        // eslint-disable-next-line no-console
                        // console.log('[TaskHistoryDialog] key:', key, 'typeof dateChanged:', typeof historyEntry.dateChanged, 'dateChanged:', historyEntry.dateChanged);
                        const dateStr = formatAnyDate(historyEntry.dateChanged);
                        const user = getUserDisplayName(historyEntry.changedBy) || 'Unknown User';
                        const deleteRestoreEvent = getDeleteRestoreEvent(historyEntry);

                        // Check for empty history (only dateChanged and maybe changedBy)
                        const entryKeys = Object.keys(historyEntry);
                        const hasNoChanges = (
                          entryKeys.length === 1 ||
                          (entryKeys.length === 2 && historyEntry.changedBy)
                        );

                        return (
                          <div key={`edit-${key}`} className="p-3 hover:bg-primary/5 dark:hover:bg-primary/10">
                            <div
                              className="flex justify-between items-start mb-2 cursor-pointer"
                              onClick={() => toggleExpand(key, 'edit')}
                            >
                              <div className="flex items-center">
                                {expanded.includes(`edit-${key}`) ?
                                  <ChevronDown className="h-4 w-4 mr-2" /> :
                                  <ChevronRight className="h-4 w-4 mr-2" />
                                }
                                <div>
                                  <div className="font-medium">{formatAnyDate(historyEntry.dateChanged)}</div>
                                  <div className="text-xs text-gray-500">{user}</div>
                                </div>
                              </div>
                              {/* Show badge for delete/restore events */}
                              {deleteRestoreEvent && (
                                <div className={`px-2 py-1 rounded text-xs font-medium ${
                                  deleteRestoreEvent.type === 'deleted' 
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' 
                                    : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                }`}>
                                  {deleteRestoreEvent.type === 'deleted' ? '🗑️ Deleted' : '↩️ Restored'}
                                </div>
                              )}
                            </div>
                            {expanded.includes(`edit-${key}`) && (
                              <div className="space-y-2 text-sm mt-2 pl-6 border-l-2 border-primary/30 dark:border-primary/50">
                                {hasNoChanges && !deleteRestoreEvent && (
                                  <div className="flex items-start gap-2">
                                    <Info className="h-4 w-4 text-primary/60 mt-0.5" />
                                    <div>
                                      <span className="text-gray-600 dark:text-gray-400 italic">Task was accessed but no specific changes were recorded</span>
                                    </div>
                                  </div>
                                )}
                                {/* Show delete/restore event message */}
                                {deleteRestoreEvent && (
                                  <div className={`flex items-start gap-2 p-3 rounded-md ${
                                    deleteRestoreEvent.type === 'deleted'
                                      ? 'bg-red-50 border border-red-200 dark:bg-red-900/10 dark:border-red-800'
                                      : 'bg-green-50 border border-green-200 dark:bg-green-900/10 dark:border-green-800'
                                  }`}>
                                    <div className="flex-shrink-0">
                                      {deleteRestoreEvent.type === 'deleted' ? (
                                        <span className="text-red-600 text-lg">🗑️</span>
                                      ) : (
                                        <span className="text-green-600 text-lg">↩️</span>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className={`font-semibold ${
                                        deleteRestoreEvent.type === 'deleted' ? 'text-red-800 dark:text-red-300' : 'text-green-800 dark:text-green-300'
                                      }`}>
                                        {deleteRestoreEvent.type === 'deleted' ? 'Task Deleted' : 'Task Restored'}
                                      </div>
                                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                        {deleteRestoreEvent.type === 'deleted' 
                                          ? `This task was marked as deleted and hidden from the main list. It can be restored from the deleted tasks view.`
                                          : `This task was restored and is now active in the main list.`
                                        }
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {/* Render changed fields (excluding delete-related fields) */}
                                {!deleteRestoreEvent && historyEntry.fieldsChanged && Object.entries(historyEntry.fieldsChanged).filter(([field, details]) => !deepEqual(details.oldValue, details.newValue) && !['isdeleted', 'deletedat', 'deletedby'].includes(field.toLowerCase())).length > 0 && (
                                  <div>
                                    <div className="font-semibold mb-1">Fields Changed:</div>
                                    <ul className="list-disc ml-5">
                                      {Object.entries(historyEntry.fieldsChanged)
                                        .filter(([field, details]) => !deepEqual(details.oldValue, details.newValue) && !['isdeleted', 'deletedat', 'deletedby'].includes(field.toLowerCase()))
                                        .map(([field, details]) => (
                                          <li key={field}>
                                            <span className="font-semibold">{details.fieldName || field}:</span>{' '}
                                            <span className="text-red-600 line-through">{renderFieldValue(details.dbFieldName, details.oldValue)}</span> → {' '}
                                            <span className="text-green-600">{renderFieldValue(details.dbFieldName, details.newValue)}</span>
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                )}
                                {/* Show indicator for edits made while deleted */}
                                {!deleteRestoreEvent && historyEntry.fieldsChanged && Object.entries(historyEntry.fieldsChanged).some(([field, details]) => !deepEqual(details.oldValue, details.newValue) && !['isdeleted', 'deletedat', 'deletedby'].includes(field.toLowerCase())) && (
                                  <div className="flex items-start gap-2">
                                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                                    <div className="text-xs text-amber-700 dark:text-amber-400 italic">
                                      This edit was made while the task was deleted
                                    </div>
                                  </div>
                                )}
                                {/* If no changed fields, show a message */}
                                {!deleteRestoreEvent && historyEntry.fieldsChanged && Object.entries(historyEntry.fieldsChanged).filter(([field, details]) => !deepEqual(details.oldValue, details.newValue) && !['isdeleted', 'deletedat', 'deletedby'].includes(field.toLowerCase())).length === 0 && (
                                  <div className="flex items-start gap-2">
                                    <Info className="h-4 w-4 text-primary/60 mt-0.5" />
                                    <div>
                                      <span className="text-gray-600 dark:text-gray-400 italic">No actual changes recorded in this entry</span>
                                    </div>
                                  </div>
                                )}
                                {/* Example: comments or other custom fields can be rendered here as needed */}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Perform History Section */}
              <div className="border rounded-md overflow-hidden">
                <button
                  onClick={() => toggleSection('performHistory')}
                  className="w-full text-left bg-primary/15 dark:bg-primary/20 text-primary p-3 font-medium text-sm flex justify-between items-center hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
                  aria-expanded={expandedSections.performHistory}
                >
                  <span>Perform History</span>
                  {expandedSections.performHistory ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                {sortedPerformEntries.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No perform history available for this task.
                  </div>
                ) : expandedSections.performHistory ? (
                  <div className="flex flex-col">
                    <div className="divide-y">
                      {sortedPerformEntries.map(([key, entry]: [string, PerformHistoryItem]) => {
                        // Use performedDate as the primary date, fall back to others if not available
                        const latestDate = entry.performedDate ||
                          entry.checkDate ||
                          entry.lastCheck ||
                          entry.scheduledDate ||
                          null;

                        const user = entry.performedBy ? getUserDisplayName(entry.performedBy) : 'System';
                        const dateStr = latestDate ? formatAnyDate(latestDate) : 'No date';

                        return (
                          <div key={`perform-${key}`} className="p-3 hover:bg-primary/5 dark:hover:bg-primary/10">
                            <div
                              className="flex justify-between items-start mb-2 cursor-pointer"
                              onClick={() => toggleExpand(key, 'perform')}
                            >
                              <div className="flex items-center">
                                {expanded.includes(`perform-${key}`) ?
                                  <ChevronDown className="h-4 w-4 mr-2" /> :
                                  <ChevronRight className="h-4 w-4 mr-2" />
                                }
                                <div>
                                  <div className="font-medium">
                                    {entry.performedDate ? formatAnyDate(entry.performedDate) : dateStr}
                                    {entry.performedDate && entry.scheduledDate && (
                                      <span className="ml-2 text-xs text-gray-500">
                                        (scheduled: {formatAnyDate(entry.scheduledDate)})
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">{user}</div>
                                </div>
                              </div>
                            </div>
                            {expanded.includes(`perform-${key}`) && (
                              <div className="space-y-2 text-sm mt-2 pl-6 border-l-2 border-primary/30 dark:border-primary/50">
                                {/* Display check information */}
                                {entry.check && (
                                  <div className="mb-2">
                                    <div className="font-semibold">Check:</div>
                                    <div className="text-gray-700 dark:text-gray-300">{entry.check}</div>
                                  </div>
                                )}

                                {/* Display dates */}
                                <div className="grid grid-cols-2 gap-2">
                                  {entry.checkDate && (
                                    <div>
                                      <div className="text-xs text-gray-500">Check Date:</div>
                                      <div>{formatAnyDate(entry.checkDate)}</div>
                                    </div>
                                  )}
                                  {entry.lastCheck && (
                                    <div>
                                      <div className="text-xs text-gray-500">Last Check:</div>
                                      <div>{formatAnyDate(entry.lastCheck)}</div>
                                    </div>
                                  )}
                                  {entry.performedDate && (
                                    <div>
                                      <div className="text-xs text-gray-500">Performed:</div>
                                      <div>{formatAnyDate(entry.performedDate)}</div>
                                    </div>
                                  )}
                                  {entry.scheduledDate && (
                                    <div>
                                      <div className="text-xs text-gray-500">Scheduled:</div>
                                      <div>{formatAnyDate(entry.scheduledDate)}</div>
                                    </div>
                                  )}
                                </div>

                                {/* Display additional fields */}
                                {entry.comments && (
                                  <div className="mt-2">
                                    <div className="font-semibold">Comments:</div>
                                    <div className="text-gray-700 dark:text-gray-300">{entry.comments}</div>
                                  </div>
                                )}

                                {entry.evidence && entry.evidence !== 'None' && (
                                  <div className="mt-2">
                                    <div className="font-semibold">Evidence:</div>
                                    <div className="text-gray-700 dark:text-gray-300 flex flex-col gap-1">
                                      {Array.isArray(entry.evidence) ? (
                                        entry.evidence.map((ev, idx) => (
                                          <EvidenceDownloadLink key={ev + idx} evidencePath={ev} fileName={ev.split('/').pop() || `evidence_${idx + 1}`} />
                                        ))
                                      ) : (
                                        <EvidenceDownloadLink evidencePath={entry.evidence} fileName={entry.evidence.split('/').pop() || 'evidence'} />
                                      )}
                                    </div>
                                  </div>
                                )}

                                {entry.dataClassification && entry.dataClassification !== 'None' && (
                                  <div className="mt-2">
                                    <div className="font-semibold">Data Classification:</div>
                                    <div className="text-gray-700 dark:text-gray-300">{entry.dataClassification}</div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <Button variant="outline" onClick={onClose} className="w-full text-center justify-center">
              Close
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog >
  );
};

// Helper component for evidence download link
const EvidenceDownloadLink: React.FC<{ evidencePath: string; fileName: string }> = ({ evidencePath, fileName }) => {
  // TODO: Replace with real Firebase config and storageRef if available in context
  const [url, setUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getEvidenceUrl({ fileName, path: evidencePath })
      .then((generatedUrl) => {
        if (!cancelled) setUrl(generatedUrl);
      })
      .catch((err) => {
        if (!cancelled) setError('Could not generate download link');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [evidencePath, fileName]);

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await downloadEvidence({ fileName, path: evidencePath });
    } catch (err) {
      setError('Download failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <span className="italic text-xs text-gray-400">Loading...</span>;
  if (error) return <span className="text-red-500 text-xs">{error}</span>;
  if (!url) return null;
  return (
    <a
      href={url}
      download={fileName}
      className="text-primary underline hover:text-primary/80 cursor-pointer flex items-center gap-1"
      onClick={handleDownload}
      target="_blank"
      rel="noopener noreferrer"
    >
      <FileText className="inline-block h-4 w-4 mr-1" />
      {fileName}
    </a>
  );
};

export default TaskHistoryDialog;
